import { useState } from "react";
import { Link } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dumbbell, ArrowLeft, Mail, Loader2, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      toast.success("Tjek din email for nulstillingslink");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Der opstod en fejl");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1534438327276-14e5300c3a48?crop=entropy&cs=srgb&fm=jpg&ixid=M3w0NjA3ODB8MHwxfHNlYXJjaHwxfHxneW0lMjBkYXJrfGVufDB8fHx8MTcwOTMwMDAwMHww&ixlib=rb-4.0.3&q=85"
            alt="Gym"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/90" />
        </div>
        
        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-16">
          <div className="flex items-center gap-3 mb-12">
            <div className="w-12 h-12 rounded-lg bg-[#DFFF00] flex items-center justify-center">
              <Dumbbell className="h-6 w-6 text-black" />
            </div>
            <span style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-2xl uppercase tracking-wider text-white">
              Fitrack<span className="text-[#DFFF00]">R</span>
            </span>
          </div>

          <h2 style={{ fontFamily: 'Oswald, sans-serif' }} className="text-4xl xl:text-5xl font-bold uppercase text-white leading-tight mb-6">
            Glemt din<br />
            <span className="text-[#DFFF00]">adgangskode?</span>
          </h2>

          <p className="text-zinc-400 text-lg max-w-md">
            Ingen problem! Indtast din email, så sender vi dig et link til at nulstille din adgangskode.
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <Link to="/login" className="inline-flex items-center gap-2 mb-8 text-zinc-500 hover:text-white transition-colors text-sm" data-testid="forgot-back">
            <ArrowLeft className="h-4 w-4" /> Tilbage til login
          </Link>

          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-6 lg:hidden">
            <Dumbbell className="h-6 w-6 text-[#DFFF00]" />
            <span style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-lg uppercase tracking-wider text-white">
              Fitrack<span className="text-[#DFFF00]">R</span>
            </span>
          </div>

          {sent ? (
            <div className="text-center" data-testid="forgot-success">
              <div className="w-20 h-20 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-green-400" />
              </div>
              <h1 style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-3xl uppercase tracking-tight text-white mb-4">
                Email sendt!
              </h1>
              <p className="text-zinc-400 mb-8">
                Vi har sendt et nulstillingslink til <span className="text-white">{email}</span>. 
                Tjek din indbakke (og evt. spam-mappe).
              </p>
              <p className="text-sm text-zinc-500 mb-6">
                Linket udløber om 1 time.
              </p>
              <Button 
                onClick={() => setSent(false)}
                variant="outline"
                className="border-zinc-700 text-zinc-300 hover:text-white hover:border-zinc-500"
              >
                Prøv igen med anden email
              </Button>
            </div>
          ) : (
            <>
              <div className="mb-8 p-4 bg-gradient-to-r from-blue-500/10 to-blue-500/5 border border-blue-500/20 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                    <Mail className="h-6 w-6 text-blue-400" />
                  </div>
                  <div>
                    <p style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-lg uppercase text-blue-400">
                      Nulstil Adgangskode
                    </p>
                    <p className="text-sm text-zinc-400">
                      Vi sender dig et link via email
                    </p>
                  </div>
                </div>
              </div>

              <h1 style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-3xl uppercase tracking-tight text-white mb-2" data-testid="forgot-title">
                Glemt adgangskode
              </h1>
              <p className="text-zinc-400 mb-8">
                Indtast din email for at modtage et nulstillingslink.
              </p>

              <form onSubmit={handleSubmit} className="space-y-5" data-testid="forgot-form">
                <div>
                  <Label htmlFor="email" className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5 block">Email</Label>
                  <Input 
                    id="email" 
                    type="email" 
                    value={email} 
                    onChange={e => setEmail(e.target.value)} 
                    required
                    className="bg-zinc-900/50 border-zinc-800 focus:border-[#DFFF00] focus:ring-[#DFFF00]/20 rounded-lg h-12 text-white placeholder:text-zinc-600" 
                    data-testid="forgot-email" 
                    placeholder="din@email.dk" 
                  />
                </div>

                <Button 
                  type="submit" 
                  disabled={loading} 
                  data-testid="forgot-submit"
                  className="w-full bg-[#DFFF00] text-black hover:bg-[#C8E600] hover:shadow-[0_0_30px_rgba(223,255,0,0.3)] rounded-lg font-bold uppercase tracking-wider h-14 text-base transition-all duration-300"
                >
                  {loading ? (
                    <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Sender...</>
                  ) : (
                    "Send nulstillingslink"
                  )}
                </Button>
              </form>

              <p className="mt-8 text-center text-sm text-zinc-500">
                Huskede du din adgangskode?{" "}
                <Link to="/login" className="text-[#DFFF00] hover:underline font-medium" data-testid="forgot-login-link">
                  Log ind her
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
